import React, { useState, useEffect, useMemo } from "react";
import { ScrollView, View, StyleSheet, TextInput } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { Menu } from "@/components/Menu";
import Badge from "@/components/ui/Badge";
import { listService, Market } from "@/services/listService";
import useAuth from "@/hooks/useAuth";
import { useLocalSearchParams, router } from "expo-router";
import { getEntityPermissions } from "@/lib/permissions";
import { NoPermissionScreen } from "@/components/screens/shared/NoPermissionScreen";
import { useSettingsSearch } from "@/contexts/SettingsSearchContext";

export default function MarketsPage() {
  const { colors } = useTheme();
  const { tenantId, userRoles } = useAuth();
  const permissions = getEntityPermissions(userRoles, "markets");
  const params = useLocalSearchParams<{ create?: string }>();

  const [markets, setMarkets] = useState<Market[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { searchValue } = useSettingsSearch("markets");

  const filteredMarkets = useMemo(() => {
    if (!searchValue.trim()) return markets;
    const search = searchValue.toLowerCase();
    return markets.filter(
      (item) => item.name?.toLowerCase().includes(search) || item.fips_code?.toLowerCase().includes(search),
    );
  }, [markets, searchValue]);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<{ name: string; fips_code: string; sage_name: string }>({
    name: "",
    fips_code: "",
    sage_name: "",
  });

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await listService.getMarkets();
      setMarkets(data);
    } catch (err: any) {
      setError(err.message || "Failed to load markets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Watch for create param changes
  useEffect(() => {
    if (params.create === "true") {
      setShowForm(true);
      setEditingId(null);
      setFormData({ name: "", fips_code: "", sage_name: "" });
    }
  }, [params.create]);

  // Menu actions
  const handleEdit = (market: Market) => {
    setEditingId(market.id);
    setFormData({
      name: market.name,
      fips_code: market.fips_code || "",
      sage_name: (market.meta_data as any)?.sage_name || "",
    });
    setShowForm(true);
    setError(null);
  };

  const handleDuplicate = (market: Market) => {
    setEditingId(null); // No ID means it's a new item
    setFormData({
      name: market.name,
      fips_code: market.fips_code || "",
      sage_name: (market.meta_data as any)?.sage_name || "",
    });
    setShowForm(true);
    setError(null);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this market?")) return;

    try {
      await listService.deleteMarket(id);
      setMarkets(markets.filter((market) => market.id !== id));
    } catch (err: any) {
      setError(err.message || "Failed to delete market");
    }
  };

  // Form submit handler (handles both add and update)
  const handleSubmit = async () => {
    if (!tenantId || !formData.name.trim()) {
      setError("Name is required");
      return;
    }

    try {
      const metaPatch = { sage_name: formData.sage_name.trim() || undefined };

      if (editingId) {
        const existing = markets.find((m) => m.id === editingId);
        const updated = await listService.updateMarket(editingId, {
          name: formData.name.trim(),
          fips_code: formData.fips_code.trim() || undefined,
          meta_data: { ...(existing?.meta_data || {}), ...metaPatch },
        });
        setMarkets(markets.map((market) => (market.id === editingId ? updated : market)));
      } else {
        const market = await listService.addMarket({
          name: formData.name.trim(),
          fips_code: formData.fips_code.trim() || undefined,
          tenant_id: tenantId,
          meta_data: metaPatch,
        });
        setMarkets([...markets, market]);
      }

      // Reset form
      setFormData({ name: "", fips_code: "", sage_name: "" });
      setShowForm(false);
      setEditingId(null);
      setError(null);
      router.setParams({ create: undefined });
    } catch (err: any) {
      setError(err.message || (editingId ? "Failed to update market" : "Failed to add market"));
    }
  };

  const handleCancel = () => {
    setFormData({ name: "", fips_code: "", sage_name: "" });
    setShowForm(false);
    setEditingId(null);
    setError(null);
    router.setParams({ create: undefined });
  };

  if (!permissions.read) {
    return <NoPermissionScreen entityName="markets" />;
  }

  return (
    <ScrollView style={styles.container}>
      <View>
        {loading ? (
          <ThemedText style={styles.centered}>Loading markets...</ThemedText>
        ) : error ? (
          <View style={styles.errorContainer}>
            <ThemedText type="defaultSemiBold" style={{ color: colors.red.text }}>
              {error}
            </ThemedText>
            <Button text="Retry" icon="refresh" style={{ marginTop: 8 }} onPress={load} />
          </View>
        ) : (
          <View style={styles.cardsContainer}>
            {permissions.create && showForm && !editingId && (
              <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
                <View style={styles.editForm}>
                  <View style={styles.row}>
                    <TextInput
                      style={[
                        styles.input,
                        styles.flexInput,
                        { borderColor: colors.border, color: colors.text, backgroundColor: colors.backgroundSecondary },
                      ]}
                      placeholder="Name *"
                      placeholderTextColor={colors.placeholder}
                      value={formData.name}
                      onChangeText={(text) => setFormData({ ...formData, name: text })}
                      autoFocus
                    />
                    <TextInput
                      style={[
                        styles.input,
                        styles.fipsInput,
                        { borderColor: colors.border, color: colors.text, backgroundColor: colors.backgroundSecondary },
                      ]}
                      placeholder="FIPS Code"
                      placeholderTextColor={colors.placeholder}
                      value={formData.fips_code}
                      onChangeText={(text) => setFormData({ ...formData, fips_code: text })}
                    />
                    <TextInput
                      style={[
                        styles.input,
                        styles.sageInput,
                        { borderColor: colors.border, color: colors.text, backgroundColor: colors.backgroundSecondary },
                      ]}
                      placeholder="Sage Value"
                      placeholderTextColor={colors.placeholder}
                      value={formData.sage_name}
                      onChangeText={(text) => setFormData({ ...formData, sage_name: text })}
                    />
                  </View>
                  <View style={styles.editActions}>
                    <Button
                      text="Cancel"
                      style={[styles.smallBtn, { borderColor: colors.border }]}
                      textStyle={{ fontSize: 13 }}
                      onPress={handleCancel}
                    />
                    <Button
                      text="Add"
                      icon="plus"
                      iconPosition="right"
                      style={[styles.smallBtn, { backgroundColor: colors.primary }]}
                      textStyle={{ fontSize: 13, color: "white" }}
                      onPress={handleSubmit}
                      enabled={!!formData.name.trim()}
                    />
                  </View>
                </View>
              </View>
            )}

            {filteredMarkets.map((market) => {
              const isEditing = editingId === market.id;

              return (
                <View
                  key={market.id}
                  style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}
                >
                  {isEditing ? (
                    <View style={styles.editForm}>
                      <View style={styles.row}>
                        <TextInput
                          style={[
                            styles.input,
                            styles.flexInput,
                            {
                              borderColor: colors.border,
                              color: colors.text,
                              backgroundColor: colors.backgroundSecondary,
                            },
                          ]}
                          placeholder="Name *"
                          placeholderTextColor={colors.placeholder}
                          value={formData.name}
                          onChangeText={(text) => setFormData({ ...formData, name: text })}
                          autoFocus
                        />
                        <TextInput
                          style={[
                            styles.input,
                            styles.fipsInput,
                            {
                              borderColor: colors.border,
                              color: colors.text,
                              backgroundColor: colors.backgroundSecondary,
                            },
                          ]}
                          placeholder="FIPS Code"
                          placeholderTextColor={colors.placeholder}
                          value={formData.fips_code}
                          onChangeText={(text) => setFormData({ ...formData, fips_code: text })}
                        />
                        <TextInput
                          style={[
                            styles.input,
                            styles.sageInput,
                            {
                              borderColor: colors.border,
                              color: colors.text,
                              backgroundColor: colors.backgroundSecondary,
                            },
                          ]}
                          placeholder="Sage Value"
                          placeholderTextColor={colors.placeholder}
                          value={formData.sage_name}
                          onChangeText={(text) => setFormData({ ...formData, sage_name: text })}
                        />
                      </View>
                      <View style={styles.editActions}>
                        <Button
                          text="Cancel"
                          style={[styles.smallBtn, { borderColor: colors.border }]}
                          textStyle={{ fontSize: 13 }}
                          onPress={handleCancel}
                        />
                        <Button
                          text="Update"
                          icon="check"
                          iconPosition="right"
                          style={[styles.smallBtn, { backgroundColor: colors.primary }]}
                          textStyle={{ fontSize: 13, color: "white" }}
                          onPress={handleSubmit}
                          enabled={!!formData.name.trim()}
                        />
                      </View>
                    </View>
                  ) : (
                    <View style={styles.itemContent}>
                      <View style={styles.itemInfo}>
                        <View style={styles.itemHeader}>
                          <ThemedText type="defaultSemiBold">{market.name}</ThemedText>
                          {market.fips_code && <Badge label="FIPS" tone="indigo" text={market.fips_code} />}
                          {(market.meta_data as any)?.sage_name && (
                            <Badge label="Sage" tone="green" text={(market.meta_data as any).sage_name} />
                          )}
                          <Menu
                            buttonStyle={{
                              marginLeft: "auto",
                              alignSelf: "flex-start",
                              borderWidth: 0,
                              backgroundColor: "transparent",
                            }}
                            hideArchive={true}
                            canEdit={permissions.update}
                            canDuplicate={permissions.create}
                            canDelete={permissions.delete}
                            onEdit={() => handleEdit(market)}
                            onDuplicate={() => handleDuplicate(market)}
                            onDelete={() => handleDelete(market.id)}
                          />
                        </View>
                      </View>
                    </View>
                  )}
                </View>
              );
            })}
          </View>
        )}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    marginTop: 16,
  },
  cardsContainer: {
    gap: 12,
  },
  card: {
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 12,
    overflow: "hidden",
  },
  itemContent: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
  },
  itemInfo: {
    flex: 1,
    gap: 4,
  },
  itemHeader: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: 8,
  },
  editForm: {
    padding: 16,
    gap: 12,
  },
  row: {
    flexDirection: "row",
    gap: 8,
  },
  input: {
    height: 40,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
  },
  flexInput: {
    flex: 1,
  },
  fipsInput: {
    width: 120,
  },
  sageInput: {
    width: 160,
  },
  editActions: {
    flexDirection: "row",
    gap: 8,
    justifyContent: "flex-end",
  },
  smallBtn: {
    height: 32,
    paddingHorizontal: 16,
    backgroundColor: "transparent",
    borderWidth: 1,
  },
  centered: {
    textAlign: "center",
    paddingVertical: 32,
    opacity: 0.7,
  },
  errorContainer: {
    alignItems: "center",
    paddingVertical: 32,
  },
});
